import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { useAutomationTemplatesByStatusQuery } from '../../../../../queries/AutomationTemplate/useAutomationTemplatesByStatus';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
} from '../../../../components-v2/Combobox';
import { AutomationSteps } from '../../../../components/automation/AutomationSteps';
import { baseStyleSheet } from '../../../../styles/styles';
import { Collapsible } from '../../../../components/Collapsible';
import { InputFieldError } from '../../../../components/InputFieldError';
import { AdvancedOptionsButton } from '../../../../components/automation/AutomationStepCard/presentation';
import { StepSendFromOption } from '../../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { styleSheet as stepCardStyles } from '../../../../components/automation/AutomationStepCard/styles';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useAutoSave } from '../../hooks/useAutoSave';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { triggerAutomationStepRendererStyles as styles } from './styles/triggerAutomationStepRendererStyles';

interface TriggerAutomationStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

type TriggerAutomationStepModel = Api.IAutomationStep & {
	targetAutomationTemplateId?: string;
	targetAutomationTemplateName?: string;
};

type AutomationTemplateTargetSummary = Api.IAutomationTemplate & {
	publishedSteps?: Api.IAutomationStepReference[];
	draftSteps?: Api.IAutomationStepReference[];
	runtimeDays?: number;
};

function isManualOrTagTriggered(trigger: Api.IAutomationTrigger | undefined | null): boolean {
	if (!trigger) {
		// Published template summaries can omit the trigger object for manual automations.
		return true;
	}
	const triggerType = (trigger as { _type?: string })._type;
	return (
		triggerType === Api.AutomationTriggerType.Manual ||
		triggerType === 'ManualAutomationTrigger' ||
		triggerType === Api.AutomationTriggerType.Tag ||
		triggerType === 'TagAutomationTrigger'
	);
}

function getPublishedTrigger(template: Api.IAutomationTemplate): Api.IAutomationTrigger | undefined | null {
	const templateLike = template as {
		published?: { trigger?: Api.IAutomationTrigger | null };
		publishedTrigger?: Api.IAutomationTrigger | null;
	};

	if ('publishedTrigger' in templateLike) {
		return templateLike.publishedTrigger;
	}

	return templateLike.published?.trigger;
}

function isEligibleTargetTemplate(template: Api.IAutomationTemplate, currentTemplateId?: string): boolean {
	if (!template?.id) {
		return false;
	}
	if (currentTemplateId && template.id === currentTemplateId) {
		return false;
	}

	const publishedTrigger = getPublishedTrigger(template);
	return isManualOrTagTriggered(publishedTrigger);
}

export function TriggerAutomationStepRenderer({
	nodeId,
	data,
	readOnly: readOnlyProp,
}: TriggerAutomationStepRendererProps) {
	const { step, template, canEdit, readonly, trigger, errorMessages, stepData, stepId } =
		useStepRenderer<TriggerAutomationStepModel>(data, readOnlyProp);

	const triggerStep = step;
	const { updateNodeData, impersonationContext } = useAutomationFlowContext();
	const schedule = triggerStep?.schedule || stepData?.schedule;

	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		impersonationContext,
	});

	const { data: publishedTemplates, isLoading: isLoadingTemplates } = useAutomationTemplatesByStatusQuery({
		status: Api.TemplateStatus.Published,
		impersonationContext,
	});

	const eligibleTemplates = React.useMemo<AutomationTemplateTargetSummary[]>(() => {
		const templates = (publishedTemplates || []) as AutomationTemplateTargetSummary[];
		return templates.filter(candidate => isEligibleTargetTemplate(candidate, template?.id));
	}, [publishedTemplates, template?.id]);

	const selectedTargetId = triggerStep?.targetAutomationTemplateId || '';
	const selectedTemplate = React.useMemo<AutomationTemplateTargetSummary | null>(() => {
		return eligibleTemplates.find(candidate => candidate.id === selectedTargetId) ?? null;
	}, [eligibleTemplates, selectedTargetId]);

	const saveStep = React.useCallback(
		(updatedFields: Partial<TriggerAutomationStepModel>) => {
			if (!triggerStep || !stepId) {
				return;
			}

			const updated: TriggerAutomationStepModel = {
				...triggerStep,
				...updatedFields,
			};

			updateNodeData?.(nodeId, {
				automationStep: updated,
			});

			autoSave.saveStep(stepId, data.stepType, updated as Api.IAutomationStep);
		},
		[triggerStep, stepId, updateNodeData, nodeId, autoSave, data.stepType]
	);

	const automationComboboxId = `trigger-automation-combobox-${nodeId}`;

	const handleTargetTemplateSelected = React.useCallback(
		(chosen: unknown) => {
			if (readonly) {
				return;
			}

			const chosenTemplate = (chosen ?? null) as AutomationTemplateTargetSummary | null;

			saveStep({
				targetAutomationTemplateId: chosenTemplate?.id || '',
				targetAutomationTemplateName: chosenTemplate?.name || '',
			});
		},
		[readonly, saveStep]
	);

	const handleScheduleChange = React.useCallback(
		(newSchedule: Api.IAutomationStepSchedule) => {
			saveStep({ schedule: newSchedule });
		},
		[saveStep]
	);

	const handleSendFromChange = React.useCallback(
		(option: Api.ISendFromOptions | null) => {
			saveStep({ sendFromOptions: option });
		},
		[saveStep]
	);

	const [comboboxOpen, setComboboxOpen] = React.useState(false);
	const pickerRef = React.useRef<HTMLDivElement>(null);
	React.useEffect(() => {
		const element = pickerRef.current;
		if (!element || readonly) {
			return;
		}
		const handler = () => {
			setComboboxOpen(true);
		};
		element.addEventListener('mousedown', handler, true);
		return () => {
			element.removeEventListener('mousedown', handler, true);
		};
	}, [readonly]);

	const [advancedOpen, setAdvancedOpen] = React.useState(() => {
		return !!triggerStep?.sendFromOptions;
	});

	React.useEffect(() => {
		setAdvancedOpen(!!triggerStep?.sendFromOptions);
	}, [triggerStep?.sendFromOptions]);

	if (!triggerStep) {
		return <div>Loading step data… Try collapsing and re-expanding this node.</div>;
	}

	const showRequiredError = !readonly && !triggerStep.targetAutomationTemplateId;

	return (
		<div className={css(styles.container)}>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>

			{triggerStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={data.stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={triggerStep as Api.IAutomationStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
				/>
			)}

			<label className={css(stepCardStyles.fieldLabel)} htmlFor={automationComboboxId}>
				Automation to start
			</label>
			<p className={css(styles.helpText)}>
				This step starts another automation for the same contact. This must be the last step.
			</p>
			<p className={css(styles.helpText)}>
				If the contact is currently in another automation (excluding this one), this step is skipped.
			</p>

			{isLoadingTemplates ? (
				<div className={css(styles.loadingText)}>Loading automations…</div>
			) : (
				<div ref={pickerRef} className={css(styles.automationPicker)}>
					<Combobox
						items={eligibleTemplates}
						value={selectedTemplate}
						onValueChange={handleTargetTemplateSelected}
						itemToStringLabel={item => (item as AutomationTemplateTargetSummary | null)?.name ?? ''}
						isItemEqualToValue={(item, value) => {
							const itemTemplate = item as AutomationTemplateTargetSummary;
							const valueTemplate = value as AutomationTemplateTargetSummary | null;
							return !!valueTemplate && itemTemplate.id === valueTemplate.id;
						}}
						disabled={readonly}
						open={comboboxOpen}
						onOpenChange={setComboboxOpen}
					>
						<ComboboxInput
							id={automationComboboxId}
							placeholder={eligibleTemplates.length === 0 ? 'No eligible automations' : 'Search automations…'}
							disabled={readonly}
							showClear
							styles={[baseStyleSheet.wFull, styles.comboboxInputGroupOverride]}
						/>
						<ComboboxContent>
							<ComboboxStatus>
								{eligibleTemplates.length === 0
									? 'No eligible automations'
									: `${eligibleTemplates.length} automation${eligibleTemplates.length === 1 ? '' : 's'}`}
							</ComboboxStatus>
							<ComboboxEmpty>No automations found</ComboboxEmpty>
							<ComboboxList>
								{templateItem => {
									const option = templateItem as AutomationTemplateTargetSummary;
									return (
										<ComboboxItem key={option.id} value={option}>
											<ComboboxItemRadioIndicator />
											<span className={css(styles.templateOptionText)}>{option.name}</span>
										</ComboboxItem>
									);
								}}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>

					{selectedTemplate && (
						<div className={css(styles.selectedAutomationSummary)}>
							<div className={css(styles.selectedAutomationLabel)}>
								{`${selectedTemplate.publishedSteps?.length || 0} Step${
									(selectedTemplate.publishedSteps?.length || 0) === 1 ? '' : 's'
								} - ${selectedTemplate.runtimeDays || 0}-Day Runtime`}
							</div>
							<div className={css(baseStyleSheet.horizontalStack)}>
								<AutomationSteps
									publishedStepReferences={selectedTemplate.publishedSteps}
									draftStepReferences={selectedTemplate.draftSteps}
									status={selectedTemplate.status}
									maxStepsToDisplay={6}
								/>
							</div>
						</div>
					)}
				</div>
			)}

			{showRequiredError && <InputFieldError errorMessage='Select an automation to start.' />}

			<AdvancedOptionsButton isCollapsed={!advancedOpen} onClick={() => setAdvancedOpen(open => !open)} />
			<Collapsible isCollapsed={!advancedOpen}>
				<div className={css(stepCardStyles.advancedOptionsContent)}>
					<StepSendFromOption
						step={triggerStep as Api.IAutomationStep}
						isBusy={autoSave.saveStatus === 'saving'}
						onSave={(option: Api.ISendFromOptions) => {
							handleSendFromChange(option);
						}}
						connectionTypes={[]}
						sendFromLabel='Start the next automation as'
						canEdit={canEdit}
					/>
				</div>
			</Collapsible>
		</div>
	);
}
